import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '@/contexts/LanguageContext';
import { Newspaper, ExternalLink, CalendarDays } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface NewsItem {
  id: string;
  title: string;
  link: string;
  image_url: string | null;
  published_at: string;
}

const NewsSection = () => {
  const { t, lang } = useLanguage();
  const [news, setNews] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNews = async () => {
      const { data } = await supabase
        .from('news')
        .select('id, title, link, image_url, published_at')
        .order('published_at', { ascending: false })
        .limit(6);
      setNews((data as NewsItem[]) || []);
      setLoading(false);
    };
    fetchNews();

    const channel = supabase
      .channel('news-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'news' }, () => fetchNews())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, []);

  if (!loading && news.length === 0) return null;

  return (
    <section id="news" className="section-padding relative bg-background">
      <div className="container-custom">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-10">
          <p className="text-xs font-bold tracking-[0.22em] text-muted-foreground uppercase mb-2">
            {t('من موقع الأكاديمية', 'From the Academy Website')}
          </p>
          <h2 className="text-3xl md:text-5xl font-black text-primary font-heading">
            {t('آخر أخبار الأكاديمية', 'Latest Academy News')}
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto">
          {loading
            ? Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="glass-card rounded-2xl overflow-hidden animate-pulse">
                  <div className="h-40 bg-muted" />
                  <div className="p-4 space-y-2">
                    <div className="h-4 bg-muted rounded w-3/4" />
                    <div className="h-3 bg-muted rounded w-1/3" />
                  </div>
                </div>
              ))
            : news.map((n, i) => (
                <motion.a
                  key={n.id}
                  href={n.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className="glass-card-hover rounded-2xl overflow-hidden group flex flex-col"
                >
                  <div className="h-40 overflow-hidden bg-muted relative">
                    {n.image_url ? (
                      <img
                        src={n.image_url}
                        alt={n.title}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Newspaper className="w-8 h-8 text-muted-foreground/40" />
                      </div>
                    )}
                  </div>
                  <div className="p-4 flex flex-col flex-1">
                    <h3 className="text-sm font-bold text-foreground leading-relaxed line-clamp-2 flex-1">
                      {n.title}
                    </h3>
                    <div className="flex items-center justify-between mt-3 text-[11px] text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <CalendarDays className="w-3 h-3" />
                        {new Date(n.published_at).toLocaleDateString(lang === 'ar' ? 'ar-SA' : 'en-US', { day: 'numeric', month: 'long', year: 'numeric' })}
                      </span>
                      <span className="flex items-center gap-1 text-primary font-semibold">
                        {t('اقرئي المزيد', 'Read more')}
                        <ExternalLink className="w-3 h-3" />
                      </span>
                    </div>
                  </div>
                </motion.a>
              ))}
        </div>
      </div>
    </section>
  );
};

export default NewsSection;
